(() => {
  function initializeCart() {
    const drawer = document.querySelector('#cart-drawer');
    const content = document.querySelector('#cart-content');
    const backdrop = document.querySelector('.cart-backdrop');
    const bagButtons = Array.from(document.querySelectorAll('.bag-button'));
    if (!drawer || !content || !backdrop || !bagButtons.length || drawer.dataset.cartReady) return;
    drawer.dataset.cartReady = 'true';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('tabindex', '-1');
    backdrop.setAttribute('aria-hidden', 'true');

    const defaults = {
      emptyTitle: 'Your bag is empty.',
      emptyCopy: 'The first collection is coming soon.',
      continueShopping: 'Explore the collection',
      loading: 'Loading your bag…',
      loadError: 'Unable to load your bag. Please try again.',
      changeError: 'Unable to update your bag. Please try again.',
      retry: 'Try again',
      item: 'Item',
      bagLabel: 'Shopping bag, {count} items',
      quantity: 'Quantity for {title}',
      decrease: 'Decrease quantity of {title}',
      increase: 'Increase quantity of {title}',
      remove: 'Remove',
      removeLabel: 'Remove {title} from your bag',
      invalidQuantity: 'Enter a whole number of zero or more.',
      subtotal: 'Subtotal',
      checkout: 'Checkout'
    };
    const overrides = window.AtelierCartStrings || {};
    function text(key, values = {}) {
      let result = String(overrides[key] ?? defaults[key] ?? key);
      Object.entries(values).forEach(([name, value]) => {
        result = result.replaceAll('{' + name + '}', String(value));
      });
      return result;
    }
    function element(tag, className, value) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (value !== undefined) node.textContent = String(value);
      return node;
    }
    const root = window.Shopify?.routes?.root || '/';
    const inertStates = new Map();
    const nativeDialog = typeof HTMLDialogElement !== 'undefined' && drawer instanceof HTMLDialogElement;
    let opened = false;
    let returnFocus = null;
    let previousOverflow = '';
    let lastCart = null;
    let mutationBusy = false;
    let readController = null;
    let readVersion = 0;

    function updateCount(cart) {
      const count = Math.max(0, Number(cart.item_count) || 0);
      const nodes = new Set(document.querySelectorAll('[data-cart-count]'));
      bagButtons.forEach(button => {
        const countNode = button.querySelector('span');
        if (countNode) nodes.add(countNode);
        button.setAttribute('aria-label', text('bagLabel', { count }));
      });
      nodes.forEach(node => { node.textContent = '(' + count + ')'; });
    }
    function money(value, currency) {
      const amount = (Number(value) || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency
        }).format(amount);
      } catch {
        return amount.toFixed(2) + (currency ? ' ' + String(currency) : '');
      }
    }
    function safeUrl(value, sameOrigin = false) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, window.location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        if (sameOrigin && url.origin !== window.location.origin) return null;
        return url.href;
      } catch { return null; }
    }
    function setBusy(busy) {
      content.setAttribute('aria-busy', String(busy));
      content.querySelectorAll('[data-cart-change]').forEach(control => { control.disabled = busy; });
      const checkout = content.querySelector('[data-cart-checkout]');
      if (checkout) {
        if (busy) {
          checkout.removeAttribute('href');
          checkout.setAttribute('aria-disabled', 'true');
          checkout.tabIndex = -1;
        } else {
          checkout.href = '/checkout';
          checkout.removeAttribute('aria-disabled');
          checkout.removeAttribute('tabindex');
        }
      }
    }
    function showMessage(message) {
      let node = content.querySelector('[data-cart-message]');
      if (!node) {
        node = element('p', 'cart-message');
        node.dataset.cartMessage = '';
        content.append(node);
      }
      node.hidden = false;
      node.setAttribute('role', 'alert');
      node.textContent = message;
    }
    function restoreControl(focus) {
      if (!opened || !focus) return;
      const control = Array.from(content.querySelectorAll('[data-cart-change]')).find(node =>
        node.dataset.cartKey === focus.key && node.dataset.cartAction === focus.action);
      (control || content.querySelector('[data-cart-change], .cart-continue, [data-cart-checkout]') || drawer.querySelector('[data-cart-close]') || drawer).focus({ preventScroll: true });
    }
    function quantityControl(item, action, label, value) {
      const control = element('button', action === 'remove' ? 'cart-remove' : 'cart-quantity-button', value);
      control.type = 'button';
      control.dataset.cartChange = '';
      control.dataset.cartKey = item.key;
      control.dataset.cartAction = action;
      control.setAttribute('aria-label', label);
      control.addEventListener('click', () => {
        const quantity = action === 'remove' ? 0 : Number(item.quantity) + (action === 'decrease' ? -1 : 1);
        changeQuantity(item.key, Math.max(0, quantity), { key: item.key, action });
      });
      return control;
    }
    function renderCart(cart, focus) {
      updateCount(cart);
      const fragment = document.createDocumentFragment();
      if (!cart.items.length) {
        const empty = element('div', 'cart-empty');
        const link = element('a', 'cart-continue', text('continueShopping'));
        link.href = root + '#collection';
        link.addEventListener('click', () => closeDrawer(false));
        empty.append(element('p', 'cart-empty-title', text('emptyTitle')), element('p', 'cart-empty-copy', text('emptyCopy')), link);
        fragment.append(empty);
      } else {
        const lines = element('ul', 'cart-lines');
        cart.items.forEach(item => {
          const title = String(item.product_title || item.title || text('item'));
          const line = element('li', 'cart-line');
          const imageUrl = safeUrl(item.featured_image?.url || item.image);
          if (imageUrl) {
            const image = element('img', 'cart-line-image');
            image.src = imageUrl;
            image.alt = String(item.featured_image?.alt || title);
            image.width = 84;
            image.height = 108;
            image.loading = 'lazy';
            line.append(image);
          }
          const details = element('div', 'cart-line-details');
          const productUrl = safeUrl(item.url, true);
          const name = element(productUrl ? 'a' : 'span', 'cart-line-title', title);
          if (productUrl) name.href = productUrl;
          details.append(name);
          if (item.variant_title && item.variant_title !== 'Default Title') details.append(element('p', 'cart-line-variant', item.variant_title));
          const plan = item.selling_plan_allocation?.selling_plan?.name;
          if (plan) details.append(element('p', 'cart-line-variant', plan));
          const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== '' && value != null && ['string', 'number'].includes(typeof value));
          if (properties.length) {
            const list = element('dl', 'cart-line-properties');
            properties.forEach(([key, value]) => list.append(element('dt', '', key), element('dd', '', value)));
            details.append(list);
          }
          details.append(element('p', 'cart-line-price', money(item.final_line_price ?? item.line_price, cart.currency)));
          const controls = element('div', 'cart-line-controls');
          const quantity = element('input', 'cart-quantity');
          quantity.type = 'number';
          quantity.min = '0';
          quantity.step = '1';
          quantity.inputMode = 'numeric';
          quantity.value = String(item.quantity);
          quantity.dataset.cartChange = '';
          quantity.dataset.cartKey = item.key;
          quantity.dataset.cartAction = 'quantity';
          quantity.setAttribute('aria-label', text('quantity', { title }));
          quantity.addEventListener('change', () => {
            const value = quantity.valueAsNumber;
            if (!Number.isSafeInteger(value) || value < 0) {
              quantity.value = String(item.quantity);
              showMessage(text('invalidQuantity'));
              return;
            }
            changeQuantity(item.key, value, { key: item.key, action: 'quantity' });
          });
          controls.append(quantityControl(item, 'decrease', text('decrease', { title }), '-'), quantity, quantityControl(item, 'increase', text('increase', { title }), '+'), quantityControl(item, 'remove', text('removeLabel', { title }), text('remove')));
          details.append(controls);
          line.append(details);
          lines.append(line);
        });
        const summary = element('div', 'cart-summary');
        const subtotal = element('div', 'cart-subtotal');
        subtotal.append(element('span', '', text('subtotal')), element('span', '', money(cart.total_price, cart.currency)));
        const checkout = element('a', 'cart-checkout', text('checkout'));
        checkout.href = '/checkout';
        checkout.dataset.cartCheckout = '';
        summary.append(subtotal, checkout);
        fragment.append(lines, summary);
      }
      const status = element('p', 'cart-message');
      status.dataset.cartMessage = '';
      status.hidden = true;
      fragment.append(status);
      content.replaceChildren(fragment);
      setBusy(mutationBusy);
      restoreControl(focus);
    }
    async function cartResponse(response, fallback) {
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : fallback);
      if (!data || !Array.isArray(data.items)) throw new Error(fallback);
      return data;
    }
    async function fetchCart() {
      if (readController) readController.abort();
      const version = ++readVersion;
      readController = new AbortController();
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: readController.signal });
      const cart = await cartResponse(response, text('loadError'));
      if (version !== readVersion) return null;
      lastCart = cart;
      updateCount(cart);
      return cart;
    }
    async function refreshCart() {
      if (mutationBusy) return;
      content.replaceChildren(element('p', 'cart-message', text('loading')));
      content.setAttribute('aria-busy', 'true');
      try {
        const cart = await fetchCart();
        if (cart && opened) renderCart(cart);
      } catch (error) {
        if (error.name === 'AbortError' || !opened) return;
        const retry = element('button', 'cart-retry', text('retry'));
        retry.type = 'button';
        retry.addEventListener('click', refreshCart);
        content.replaceChildren(element('p', 'cart-message', text('loadError')), retry);
        content.setAttribute('aria-busy', 'false');
      }
    }
    async function changeQuantity(key, quantity, focus) {
      if (mutationBusy || !Number.isSafeInteger(quantity) || quantity < 0) return;
      mutationBusy = true;
      if (readController) readController.abort();
      ++readVersion;
      setBusy(true);
      try {
        const response = await fetch(root + 'cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: String(key), quantity }) });
        lastCart = await cartResponse(response, text('changeError'));
        updateCount(lastCart);
        mutationBusy = false;
        renderCart(lastCart, focus);
      } catch (error) {
        const message = error.message || text('changeError');
        try { await fetchCart(); } catch {}
        mutationBusy = false;
        if (lastCart) renderCart(lastCart, focus);
        if (opened) showMessage(message);
      } finally {
        mutationBusy = false;
        setBusy(false);
      }
    }
    function closeOtherPanels() {
      const search = document.querySelector('.search-button[aria-expanded="true"]');
      if (search) document.querySelector('.search-close')?.click();
      document.querySelectorAll('.currency-button[aria-expanded="true"]').forEach(button => button.click());
      const menu = document.querySelector('.menu-button[aria-expanded="true"]');
      if (menu) (document.querySelector('.menu-close') || menu).click();
    }
    function setOutsideInert() {
      inertStates.clear();
      let current = drawer;
      while (current.parentElement) {
        Array.from(current.parentElement.children).forEach(sibling => {
          if (sibling === current || sibling === backdrop || sibling.contains(backdrop) || ['SCRIPT', 'STYLE', 'LINK', 'META'].includes(sibling.tagName)) return;
          if (!inertStates.has(sibling)) inertStates.set(sibling, sibling.inert);
          sibling.inert = true;
        });
        current = current.parentElement;
        if (current === document.body) break;
      }
    }
    function focusableControls() {
      return Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(node => !node.closest('[hidden], [inert]') && node.getClientRects().length);
    }
    function focusDrawer() {
      (focusableControls()[0] || drawer).focus({ preventScroll: true });
    }
    function openDrawer(trigger) {
      if (opened) return;
      returnFocus = trigger || document.activeElement;
      closeOtherPanels();
      previousOverflow = document.body.style.overflow;
      opened = true;
      drawer.inert = false;
      drawer.hidden = false;
      backdrop.hidden = false;
      if (nativeDialog && !drawer.open) drawer.showModal();
      document.body.classList.add('cart-open');
      document.body.style.overflow = 'hidden';
      bagButtons.forEach(button => button.setAttribute('aria-expanded', 'true'));
      setOutsideInert();
      focusDrawer();
      if (mutationBusy && lastCart) renderCart(lastCart);
      else refreshCart();
    }
    function closeDrawer(restoreFocus = true) {
      if (!opened) return;
      opened = false;
      if (readController) readController.abort();
      ++readVersion;
      drawer.hidden = true;
      backdrop.hidden = true;
      if (nativeDialog && drawer.open) drawer.close();
      document.body.classList.remove('cart-open');
      document.body.style.overflow = previousOverflow;
      inertStates.forEach((value, node) => { if (node.isConnected) node.inert = value; });
      inertStates.clear();
      bagButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    }
    bagButtons.forEach(button => {
      button.setAttribute('aria-controls', 'cart-drawer');
      button.setAttribute('aria-expanded', 'false');
      const initialCount = Number((button.querySelector('span')?.textContent || '').replace(/[^0-9]/g, '')) || 0;
      button.setAttribute('aria-label', text('bagLabel', { count: initialCount }));
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDrawer(button);
      }, true);
    });
    drawer.querySelectorAll('[data-cart-close]').forEach(button => button.addEventListener('click', () => closeDrawer()));
    backdrop.addEventListener('click', () => closeDrawer());
    drawer.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
    drawer.addEventListener('close', () => closeDrawer());
    drawer.addEventListener('click', event => {
      if (!nativeDialog || event.target !== drawer) return;
      const bounds = drawer.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
    });
    document.addEventListener('keydown', event => {
      if (!opened) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeDrawer();
      } else if (event.key === 'Tab') {
        const controls = focusableControls();
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); drawer.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement) || document.activeElement === drawer)) { event.preventDefault(); first.focus(); }
      }
    }, true);
    document.addEventListener('focusin', event => { if (opened && !drawer.contains(event.target)) focusDrawer(); });
    fetchCart().catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCart, { once: true });
  else initializeCart();
})();
